import { Table, Button, Tag, Typography } from 'antd';
import { useStoreState, useStoreActions } from '../../store';
import { FormOutlined } from '@ant-design/icons';
import { MODAL_KEY } from './PackagingModalV2';
import type { PartnerPackagingV2 } from '../../store/services/PartnerService/types';
import {
  nameSorter,
  descriptionSorter,
  packagingSizeNameSorter,
  packagingIdSorter,
  partnerProvidedIdSorter,
  packagingMaterialSorter,
  linearDimensionsSorter,
  maxContentWeightSorter,
  activeSorter,
} from './packagingTableSorters';
const { Paragraph } = Typography;

export const PackagingTable = () => {
  // Data is fetched by PackagingConfiguration: this table sits inside <Card loading>, so fetching on mount would loop.
  const partnerPackaging = useStoreState((state) => state.packagingV2.partnerPackagingV2);
  const canEdit = useStoreState((state) => state.session.canEdit);
  const toggleModal = useStoreActions((action) => action.navigation.toggleModal);

  const openModal = (activePackage: PartnerPackagingV2) => {
    toggleModal({ open: true, key: MODAL_KEY, params: activePackage });
  };

  const packageColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (value: any, record: PartnerPackagingV2) => <Paragraph>{record.name}</Paragraph>,
      sorter: nameSorter,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      render: (value: any, record: PartnerPackagingV2) => <Paragraph>{record.description}</Paragraph>,
      sorter: descriptionSorter,
    },
    {
      title: 'Package Name (ID)',
      dataIndex: 'packagingName',
      render: (value: any, record: PartnerPackagingV2) => (
        <Paragraph copyable={{ text: record.packagingId }}>{record.packagingSizeName}</Paragraph>
      ),
      sorter: packagingSizeNameSorter,
    },
    {
      title: 'Packaging ID',
      dataIndex: 'packagingId',
      render: (value: any, record: PartnerPackagingV2) => (
        <Paragraph copyable={{ text: record.packagingId }}>{record.packagingId}</Paragraph>
      ),
      sorter: packagingIdSorter,
    },
    {
      title: 'Packaging ID Alias',
      dataIndex: 'partnerProvidedId',
      render: (value: any, record: PartnerPackagingV2) => (
        <Paragraph copyable={{ text: record.partnerProvidedId }}>{record.partnerProvidedId}</Paragraph>
      ),
      sorter: partnerProvidedIdSorter,
    },
    {
      title: 'Package Material',
      align: 'center',
      responsive: ['lg'],
      dataIndex: 'packagingMaterial',
      sorter: packagingMaterialSorter,
    },
    {
      title: 'Linear Dimensions',
      key: 'packageLinear',
      align: 'center',
      responsive: ['lg'],
      render: (value: any) => {
        return `${value.linearDimensions.length} x ${value.linearDimensions.width} x ${value.linearDimensions.height} ${value.linearDimensions.linearUnit}`;
      },
      sorter: linearDimensionsSorter,
    },
    {
      title: 'Max Content Weight',
      key: 'maxContent',
      align: 'center',
      responsive: ['lg'],
      render: (value: any) => {
        return `${value.maxContentWeight.weight} ${value.maxContentWeight.weightUnit}`;
      },
      sorter: maxContentWeightSorter,
    },
    {
      title: 'Active',
      dataIndex: 'fulfillmentCenters',
      align: 'center',
      filters: [
        { text: 'Active', value: true },
        { text: 'Inactive', value: false },
      ],
      onFilter: (value: any, record: PartnerPackagingV2) => record.active === value,
      sorter: activeSorter,
      render: (value: any, record: PartnerPackagingV2) =>
        record.active ? <Tag color={'green'}>Active</Tag> : <Tag color={'gold'}>Inactive</Tag>,
    },
  ];

  const actionColumns = [
    {
      title: 'Edit',
      align: 'center',
      key: 'edit',
      render: (value: any) => {
        return (
          <Button onClick={() => openModal(value)}>
            Edit <FormOutlined />
          </Button>
        );
      },
    },
  ];

  const columns = canEdit ? [...packageColumns, ...actionColumns] : packageColumns;

  return (
    <>
      <Table
        dataSource={partnerPackaging?.filter((packaging) => !packaging.archived)}
        columns={columns as any}
        rowKey="packagingId"
        loading={!partnerPackaging}
        pagination={false}
      />
    </>
  );
};
